import { useQuery } from '@apollo/client'
import React, { useEffect } from 'react'
import type { ConnectionOpen, StreamChat, UserResponse } from 'stream-chat'

import { GET_WORKSPACE_MEMBERS } from '../data/queries/members'
import { useAuth } from '../hooks/use-auth'
import { useChatClient } from '../hooks/use-chat-client'
import { useUserQuery } from '../hooks/use-user-query'
import { useWorkspaceUsers } from '../hooks/use-workspace-users'
import type { IChatUser } from '../types/chat-types'
import type { IWorkspaceMember } from '../types/user'
import { MembershipStatus } from '../types/user'
import { tokenUtils } from '../utils/token-utils'
import { useChatConnection } from './__hooks/use-chat-connection'
import { useChatUserSearch } from './__hooks/use-chat-user-search'
import { createGenericContext } from './generic-context'

type TConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error'

interface IChatContext {
  chatClient: StreamChat
  connectionStatus: TConnectionStatus
  disconnectUser: () => void
  isLoading: boolean
  mentionableUsers: UserResponse[]
  searchUser: (inputValue: string) => Promise<IChatUser[] | []>
  teamMembers: IWorkspaceMember[]
  unreadCount: number
  userConnection: ConnectionOpen | null
  workspaceUsers: (UserResponse & IChatUser)[]
}

const [useChat, ChatContextProvider] = createGenericContext<IChatContext>(
  'useChat must be used within the ChatContextProvider'
)

interface ChatProviderProps {
  children: React.ReactNode
}

/**
 * ChatProvider wires together the Stream Chat client, connection lifecycle,
 * workspace user list, and user search into a single context consumed by all
 * chat-aware components in the tree.
 *
 * Responsibilities:
 * - Bootstraps the StreamChat client via `useChatClient`
 * - Delegates connection management (connect / disconnect / reconnect) to `useChatConnection`
 * - Resolves mentionable workspace users via `useWorkspaceUsers`
 * - Provides a `searchUser` helper for @-mention search via `useChatUserSearch`
 * - Attaches and cleans up global client event listeners on mount/unmount
 */
const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { selectedWorkspace, isWorkspacesLoading } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const { myDetails: userDetails } = useUserQuery()
  const { client: chatClientService, addListeners, removeListeners } = useChatClient()
  const userChatToken = tokenUtils.getToken('User-Chat-Token')

  const { connectionStatus, unreadCount, userConnection, disconnectUser } = useChatConnection(
    chatClientService,
    userDetails,
    userChatToken
  )

  const { searchUser } = useChatUserSearch(chatClientService, userDetails)

  // Fetched lazily by `useWorkspaceUsers` — skipped here to avoid an immediate network call
  const { refetch: fetchWorkspaceMembers } = useQuery<{
    workspaceMembers: IWorkspaceMember[]
  }>(GET_WORKSPACE_MEMBERS, {
    context: { headers: { 'ws-id': workspaceID } },
    skip: true,
    variables: { input: { membershipStatuses: [MembershipStatus.ACTIVE] } }
  })

  const { mentionableUsers, teamMembers, isLoading } = useWorkspaceUsers(
    userDetails,
    chatClientService,
    workspaceID,
    fetchWorkspaceMembers,
    isWorkspacesLoading
  )

  // Attach global Stream Chat client listeners once on mount (message.new, notifications,
  // storage sync, beforeunload). Cleaned up on unmount to prevent memory leaks.
  useEffect(() => {
    addListeners()
    return () => removeListeners()
  }, [])

  const contextValue: IChatContext = {
    chatClient: chatClientService,
    connectionStatus,
    disconnectUser,
    isLoading,
    mentionableUsers,
    searchUser,
    teamMembers,
    unreadCount,
    userConnection,
    workspaceUsers: []
  }

  return <ChatContextProvider value={contextValue}>{children}</ChatContextProvider>
}

export { ChatContextProvider, ChatProvider, useChat }
